// Lathe view: the platter turns under a fixed cutting head while the groove appears in real time,
// with a "microscope" inset of the last few milliseconds of groove (both generators + the input).

import type { StatusMsg } from "./worklet";

export interface LatheGeometry {
  polar: boolean;
  rpm: number;
  diameter: number;
  innerHole: number;
  outerRad: number;
  innerRad: number;
  /** Inward advance per revolution (in). */
  pitch: number;
  /** Peak displacement (in) — drawn exaggerated so it's visible at platter scale. */
  amp: number;
  /** Grid step of the scope, as fraction of full scale (1/ampSteps), 0 = none. */
  gridStep: number;
  ampSteps: number;
}

const PLATE = 1600; // offscreen platter resolution (px)

export class LatheView {
  private ctx: CanvasRenderingContext2D;
  private plate = document.createElement("canvas");
  private pctx: CanvasRenderingContext2D;
  private raf = 0;
  private status: StatusMsg | null = null;
  private statusAt = 0;
  private cutStart: number | null = null; // engine time (s) when the cut began
  private lastCut: { x: number; y: number } | null = null;
  geo: LatheGeometry | null = null;
  /** Which generator is being monitored / drawn. */
  algo: "original" | "improved" = "improved";
  /** Needle radius while just monitoring (in). */
  monitorRadius = 5.5;
  scopeMs = 12;
  sampleRate = 44100;
  info = "";
  /** true until Start: the platter is drawn once, still */
  idle = true;

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext("2d")!;
    this.plate.width = this.plate.height = PLATE;
    this.pctx = this.plate.getContext("2d")!;
    this.clearPlate();
  }

  clearPlate() {
    const p = this.pctx;
    p.clearRect(0, 0, PLATE, PLATE);
    this.lastCut = null;
  }

  beginCut(engineNow: number) {
    this.clearPlate();
    this.cutStart = engineNow;
  }

  endCut() {
    this.cutStart = null;
  }

  get cutting() {
    return this.cutStart !== null;
  }

  /** Seconds since the cut started (engine time), or null. */
  cutSeconds(): number | null {
    if (this.cutStart === null || !this.status) return null;
    return this.now() - this.cutStart;
  }

  now(): number {
    if (!this.status) return 0;
    return this.status.now + (performance.now() - this.statusAt) / 1000;
  }

  /** Radius of the cutting head / needle right now. */
  radius(): number {
    const g = this.geo;
    if (!g) return 0;
    const s = this.cutSeconds();
    if (s !== null) return g.outerRad - (g.pitch * s * g.rpm) / 60;
    return this.monitorRadius;
  }

  onStatus(msg: StatusMsg) {
    this.status = msg;
    this.statusAt = performance.now();
    if (msg.cut && this.cutStart !== null && this.geo?.polar) this.drawCut(msg.cut[this.algo]);
  }

  /** Draw freshly cut groove points onto the platter (record coordinates). */
  private drawCut(c: { t: number[]; v: number[] }) {
    const g = this.geo!;
    const p = this.pctx;
    const s = PLATE / g.diameter, C = PLATE / 2;
    const omega = (2 * Math.PI * g.rpm) / 60;
    // wiggles are ~0.01″: exaggerate so the music is visible on the platter
    const ampDraw = Math.max(g.amp, g.pitch * 0.35);
    p.strokeStyle = "rgba(30,30,35,0.9)";
    p.lineWidth = 1;
    p.beginPath();
    if (this.lastCut) p.moveTo(this.lastCut.x, this.lastCut.y);
    for (let i = 0; i < c.t.length; i++) {
      const ts = c.t[i] - this.cutStart!;
      if (ts < 0) continue;
      const th = omega * ts;
      const r = g.outerRad - (g.pitch * th) / (2 * Math.PI) + ampDraw * c.v[i];
      const x = C + r * s * Math.cos(th), y = C - r * s * Math.sin(th);
      if (!this.lastCut) p.moveTo(x, y);
      else p.lineTo(x, y);
      this.lastCut = { x, y };
    }
    p.stroke();
  }

  start() {
    this.idle = false;
    cancelAnimationFrame(this.raf);
    const loop = () => {
      this.render();
      this.raf = requestAnimationFrame(loop);
    };
    loop();
  }

  stop() {
    cancelAnimationFrame(this.raf);
  }

  /** Back to the still, idle platter (after Stop). */
  setIdle() {
    this.idle = true;
    this.renderOnce();
  }

  /** One still frame (the idle platter before Start). */
  renderOnce() {
    requestAnimationFrame(() => this.render());
  }

  private render() {
    const cv = this.canvas, ctx = this.ctx;
    const dpr = window.devicePixelRatio || 1;
    const r0 = cv.getBoundingClientRect();
    if (cv.width !== Math.round(r0.width * dpr)) { cv.width = Math.round(r0.width * dpr); cv.height = Math.round(r0.height * dpr); }
    const W = cv.width, H = cv.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#0b0c0e";
    ctx.fillRect(0, 0, W, H);
    const g = this.geo;
    if (!g) return;
    const scopeH = Math.min(H * 0.36, 260 * dpr);
    if (g.polar) this.renderPlatter(W, H - scopeH, dpr);
    this.renderScope(0, g.polar ? H - scopeH : 0, W, g.polar ? scopeH : H, dpr);
  }

  private renderPlatter(W: number, H: number, dpr: number) {
    const ctx = this.ctx, g = this.geo!;
    const D = Math.min(W * 0.9, H * 0.92);
    const cx = W * 0.42, cy = H / 2;
    const s = D / g.diameter;
    const omega = (2 * Math.PI * g.rpm) / 60;
    const t = this.now();
    // platter rotation: during a cut, the freshly cut point sits under the head at 3 o'clock
    const rot = this.cutStart !== null ? omega * (t - this.cutStart) : omega * t;
    ctx.save();
    ctx.translate(cx, cy);
    // turntable mat
    ctx.fillStyle = "#1a1c20";
    ctx.beginPath(); ctx.arc(0, 0, D * 0.53, 0, Math.PI * 2); ctx.fill();
    ctx.rotate(rot);
    // blank disc (laser acrylic / lacquer look)
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, D / 2);
    grad.addColorStop(0, "#d9d3c4"); grad.addColorStop(1, "#c9c2b1");
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(0, 0, D / 2, 0, Math.PI * 2); ctx.fill();
    // groove band guides
    ctx.strokeStyle = "rgba(0,0,0,0.12)";
    ctx.lineWidth = 1;
    for (const r of [g.outerRad, g.innerRad]) { ctx.beginPath(); ctx.arc(0, 0, r * s, 0, Math.PI * 2); ctx.stroke(); }
    ctx.drawImage(this.plate, -D / 2, -D / 2, D, D);
    // label + hole
    ctx.fillStyle = "#2a2f38";
    ctx.beginPath(); ctx.arc(0, 0, g.innerRad * s * 0.82, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#9aa3af";
    ctx.font = `${12 * dpr}px system-ui`;
    ctx.textAlign = "center";
    ctx.fillText("RECORD LAB", 0, -g.innerRad * s * 0.35);
    ctx.fillText(`${g.rpm} rpm`, 0, g.innerRad * s * 0.45);
    ctx.fillStyle = "#0b0c0e";
    ctx.beginPath(); ctx.arc(0, 0, (g.innerHole / 2) * s, 0, Math.PI * 2); ctx.fill();
    ctx.restore();

    // cutting head / stylus at 3 o'clock, at the current radius
    const r = this.radius();
    const hx = cx + r * s, hy = cy;
    const cutting = this.cutStart !== null;
    ctx.strokeStyle = cutting ? "#ff4d4d" : "#7fd4ff";
    ctx.fillStyle = ctx.strokeStyle;
    ctx.lineWidth = 3 * dpr;
    ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(hx + 0.25 * D, hy - 0.32 * D); ctx.stroke();
    ctx.beginPath(); ctx.arc(hx, hy, 5 * dpr, 0, Math.PI * 2); ctx.fill();
    if (cutting) {
      ctx.globalAlpha = 0.25 + 0.2 * Math.sin(performance.now() / 90);
      ctx.beginPath(); ctx.arc(hx, hy, 12 * dpr, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;
    }
    // readout
    ctx.textAlign = "left";
    ctx.fillStyle = "#e6e8eb";
    const lines = [
      cutting ? "● CUTTING" : this.idle ? "idle · press ▶ Start" : "monitoring",
      `head r = ${r.toFixed(3)}″ (${(r * 25.4).toFixed(1)} mm)`,
      ...this.info.split("\n"),
    ];
    // wide: a column right of the platter; narrow (phones): a compact box in the top-left corner
    const narrow = W / dpr < 640;
    const fs = (narrow ? 10.5 : 13) * dpr, lh = (narrow ? 14 : 20) * dpr;
    ctx.font = `${fs}px system-ui`;
    const x0 = narrow ? 8 * dpr : cx + D * 0.56, y0 = narrow ? 16 * dpr : 40 * dpr;
    if (narrow) {
      const bw = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 12 * dpr;
      ctx.fillStyle = "rgba(11,12,14,0.78)";
      ctx.fillRect(x0 - 6 * dpr, y0 - fs - 2 * dpr, bw, lh * lines.length + 6 * dpr);
    }
    lines.forEach((l, i) => {
      ctx.fillStyle = i === 0 ? (cutting ? "#ff6b6b" : "#7fd4ff") : "#c8cdd4";
      ctx.fillText(l, x0, y0 + i * lh);
    });
  }

  private renderScope(x0: number, y0: number, W: number, H: number, dpr: number) {
    const ctx = this.ctx, st = this.status, g = this.geo!;
    ctx.fillStyle = "#101216";
    ctx.fillRect(x0, y0, W, H);
    ctx.strokeStyle = "#2b3038";
    ctx.strokeRect(x0 + 0.5, y0 + 0.5, W - 1, H - 1);
    ctx.fillStyle = "#8a929e";
    ctx.font = `${11 * dpr}px system-ui`;
    const narrowScope = W / dpr < 640;
    const quant = g.ampSteps ? `±${g.ampSteps} steps` : "no quantization";
    ctx.fillText(narrowScope ? `microscope · ${this.scopeMs} ms · ${quant}` : `groove microscope — last ${this.scopeMs} ms · grid = 1 ${g.polar ? "dpi step" : "step"} · ${quant}`, x0 + 10 * dpr, y0 + 16 * dpr);
    if (!st) return;
    const pad = 24 * dpr;
    const top = y0 + pad, h = H - pad * 1.4, mid = top + h / 2;
    const tEnd = st.now, tStart = tEnd - this.scopeMs / 1000;
    const X = (t: number) => x0 + ((t - tStart) / (tEnd - tStart)) * W;
    const Y = (v: number) => mid - v * (h / 2) * 0.92;
    // quantization grid
    if (g.gridStep > 0 && h / 2 / g.ampSteps > 4 * dpr) {
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      ctx.beginPath();
      for (let k = -g.ampSteps; k <= g.ampSteps; k++) { const y = Y(k * g.gridStep); ctx.moveTo(x0, y); ctx.lineTo(x0 + W, y); }
      ctx.stroke();
    }
    // source
    const src = st.src, sr = this.sampleRate;
    ctx.strokeStyle = "rgba(184,190,200,0.55)";
    ctx.lineWidth = 1 * dpr;
    ctx.beginPath();
    for (let i = 0; i < src.length; i++) {
      const t = tEnd - (src.length - i) / sr;
      if (t < tStart) continue;
      const x = X(t), y = Y(src[i]);
      if (i === 0 || t - 1 / sr < tStart) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
    const drawAlgo = (a: "original" | "improved", color: string) => {
      const { t, v } = st.scope[a];
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = (a === this.algo ? 2 : 1.2) * dpr;
      ctx.beginPath();
      let started = false;
      for (let i = 0; i < t.length; i++) {
        if (t[i] < tStart - 0.002) continue;
        const x = X(t[i]), y = Y(v[i]);
        if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
      }
      ctx.stroke();
      const spacing = t.length > 1 ? (X(t[t.length - 1]) - X(t[0])) / (t.length - 1) : 0;
      if (spacing > 5 * dpr) {
        for (let i = 0; i < t.length; i++) {
          if (t[i] < tStart) continue;
          ctx.beginPath(); ctx.arc(X(t[i]), Y(v[i]), 2.2 * dpr, 0, Math.PI * 2); ctx.fill();
        }
      }
    };
    drawAlgo("original", "#ff5a4f");
    drawAlgo("improved", "#3fa7ff");
    const ly = y0 + (narrowScope ? 31 : 16) * dpr;
    ctx.fillStyle = "#8a929e";
    ctx.fillText("input", x0 + W - 190 * dpr, ly);
    ctx.fillStyle = "#ff5a4f"; ctx.fillText("original", x0 + W - 150 * dpr, ly);
    ctx.fillStyle = "#3fa7ff"; ctx.fillText("improved", x0 + W - 90 * dpr, ly);
  }
}
